"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Navigatsioon, navItem } from "@/types/strapi-global";
import { StrapiImage } from "@/comp/_Utility/StrapiImage";

const styleMap: Record<string, string> = {
    normal: "font-light",
    special: "px-4 py-2 rounded-md border-2 border-blue-400 hover:border-pink-400 transition-all duration-500 inline-block font-medium"
};

/**
 * Resolves the destination URL for a navigation item.
 */
function getItemUrl(item: navItem): string {
    if (item.isExternalUrl) {
        return item.ItemExternalUrl || "#";
    }

    if (item.itemPageLink?.pageUrl) {
        return `/${item.itemPageLink.pageUrl}`;
    }

    return "#";
}

export default function MobileNav({ data }: { data: Navigatsioon }) {
    const [isOpen, setIsOpen] = useState(false);
    const [openCat, setOpenCat] = useState<number | null>(null);
    const pathname = usePathname();

    // Auto-close menu on route change
    useEffect(() => {
        setIsOpen(false);
    }, [pathname]);

    const categories = data?.navCategory || [];

    return (
        <div className="lg:hidden">
            {/* Top Fixed Header */}
            <header className="fixed top-0 left-0 right-0 h-[7vh] bg-pg-yellow flex justify-between items-center px-4 z-[80] shadow-sm">
                {/* Logo Link */}
                <Link
                    href="/"
                    className="h-[80%] flex items-center relative z-[85]"
                    onClick={() => setIsOpen(false)}
                >
                    {data?.logo && (
                        <StrapiImage
                            media={data.logo}
                            className="h-full w-auto object-contain"
                        />
                    )}
                </Link>

                {/* Toggle Button */}
                <button
                    type="button"
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="relative z-[85] text-3xl p-2 rounded focus:outline-none focus:ring-2 focus:ring-black cursor-pointer select-none"
                    aria-label="Toggle navigation menu"
                >
                    {isOpen ? "✕" : "☰"}
                </button>
            </header>

            {/* Invisible Right-Edge Swipe Detection Zone */}
            {!isOpen && (
                <div
                    className="fixed top-0 right-0 w-6 h-full z-[75] touch-none"
                    onTouchStart={(e) => {
                        const startX = e.touches[0].clientX;
                        const handleTouchMove = (moveEv: TouchEvent) => {
                            const currentX = moveEv.touches[0].clientX;
                            if (startX - currentX > 40) {
                                setIsOpen(true);
                                window.removeEventListener("touchmove", handleTouchMove);
                            }
                        };
                        window.addEventListener("touchmove", handleTouchMove, { once: true });
                    }}
                />
            )}

            {/* Animated Menu & Backdrop */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Dim Backdrop Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.5 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setIsOpen(false)}
                            className="fixed inset-0 bg-black z-[60] cursor-pointer"
                        />

                        {/* Slide-out Panel */}
                        <motion.aside
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 200 }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={{ left: 0, right: 0.8 }}
                            onDragEnd={(_, info) => {
                                if (info.offset.x > 100 || info.velocity.x > 500) {
                                    setIsOpen(false);
                                }
                            }}
                            className="fixed top-[7vh] right-0 bottom-0 w-[85vw] max-w-[360px] bg-white z-[70] shadow-2xl flex flex-col overflow-y-auto p-6"
                        >
                            <nav className="flex-1 space-y-6">
                                {categories.map((cat, idx) => {
                                    const navItems = cat?.navItem || [];

                                    return (
                                        <div key={idx} className="border-b border-gray-100 pb-4">
                                            {/* Category Header Button */}
                                            <button
                                                type="button"
                                                onClick={() => setOpenCat(openCat === idx ? null : idx)}
                                                className="w-full flex justify-between items-center text-xl font-semibold text-gray-800 text-left py-1 cursor-pointer"
                                            >
                                                <span className={styleMap[cat.categoryStyle] || styleMap.normal}>
                                                    {cat.categoryTitle}
                                                </span>
                                                {navItems.length > 0 && (
                                                    <span className="text-gray-500 text-lg">
                                                        {openCat === idx ? "−" : "+"}
                                                    </span>
                                                )}
                                            </button>

                                            {/* Accordion Sub-items */}
                                            <AnimatePresence>
                                                {openCat === idx && navItems.length > 0 && (
                                                    <motion.ul
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="mt-3 ml-4 space-y-3 border-l-2 border-pg-yellow pl-4 overflow-hidden"
                                                    >
                                                        {navItems.map((item, i) => {
                                                            const href = getItemUrl(item);

                                                            return (
                                                                <li key={i}>
                                                                    <Link
                                                                        href={href}
                                                                        target={item.openInNewTab ? "_blank" : undefined}
                                                                        rel={item.openInNewTab ? "noopener noreferrer" : undefined}
                                                                        className="text-base text-gray-600 hover:text-black block"
                                                                        onClick={() => setIsOpen(false)}
                                                                    >
                                                                        {item.itemTitle}
                                                                    </Link>
                                                                </li>
                                                            );
                                                        })}
                                                    </motion.ul>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </nav>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}